import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowUpRight, FiPause, FiPlay } from 'react-icons/fi';
import VoicesInteriorScene from '../voices/VoicesInteriorScene';

export function CompanyAtmosphere({ reducedMotion = false }) {
  const video = useRef(null);
  const visible = useRef(false);
  const userPaused = useRef(false);
  const manuallyStarted = useRef(false);
  const sync = useRef(() => {});
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const element = video.current;
    let disposed = false;
    let pending = false;
    manuallyStarted.current = false;
    const shouldPlay = () => !disposed && !failed && visible.current && !document.hidden && !userPaused.current
      && ((!reducedMotion && !navigator.connection?.saveData) || manuallyStarted.current);
    const syncPlayback = async () => {
      if (!shouldPlay()) { element.pause(); return; }
      if (pending || !element.paused) return;
      pending = true;
      try {
        await element.play();
        if (!shouldPlay()) element.pause();
      } catch {
        if (!disposed) setPlaying(false);
      } finally { pending = false; }
    };
    sync.current = syncPlayback;
    const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      visible.current = entries[0].isIntersecting;
      syncPlayback();
    }, { threshold: 0.06 }) : null;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      visible.current = rect.bottom > 0 && rect.top < window.innerHeight;
      syncPlayback();
    };
    observer?.observe(element);
    if (!observer) { measure(); window.addEventListener('scroll', measure, { passive: true }); }
    document.addEventListener('visibilitychange', syncPlayback);
    if (visible.current) syncPlayback();
    return () => {
      disposed = true;
      observer?.disconnect();
      window.removeEventListener('scroll', measure);
      document.removeEventListener('visibilitychange', syncPlayback);
      element.pause();
    };
  }, [reducedMotion, failed]);

  function togglePlayback() {
    const element = video.current;
    userPaused.current = !element.paused;
    manuallyStarted.current = element.paused;
    sync.current();
  }

  function jumpToTeam(event) {
    const team = document.getElementById('company-team');
    if (!team) return;
    event.preventDefault();
    team.scrollIntoView({ behavior: reducedMotion ? 'instant' : 'smooth', block: 'start' });
    team.focus({ preventScroll: true });
  }

  function onMediaError() {
    setFailed(true);
    setPlaying(false);
  }

  return <section className="vc-film" id="company-film" aria-labelledby="vc-film-title" data-ready={ready && !failed}>
    <div className="vc-film-backdrop" aria-hidden="true">
      <img src="/assets/voices/company-poster.webp" alt="" loading="lazy" decoding="async" width="1280" height="720" />
      <video ref={video} playsInline muted loop preload="none" poster="/assets/voices/company-poster.webp" tabIndex={-1} disablePictureInPicture
        onPlaying={() => { setPlaying(true); setReady(true); }} onPause={() => setPlaying(false)} onError={onMediaError}>
        <source src="/assets/voices/company-background.mp4" type="video/mp4" onError={onMediaError} />
      </video>
    </div>
    <div className="vc-film-content">
      <p className="vc-label">Who we are</p>
      <h2 id="vc-film-title">Technology connects.<br /><em>People make it work.</em></h2>
      <p className="vc-film-statement">Antrazal is built on trust, powered by people, and driven by innovation delivering solutions that create real and lasting impact.</p>
      <a href="#company-team" className="vc-text-link" onClick={jumpToTeam}>Meet the team <FiArrowUpRight aria-hidden="true" /></a>
    </div>
    <div className="vc-film-bottom">
      <p>Trust. Innovation. People.</p>
      {failed ? <p role="status">Video unavailable. Showing a still image.</p> : <button type="button" className="vc-film-toggle" onClick={togglePlayback} aria-label={`${playing ? 'Pause' : 'Play'} background video`}>
        {playing ? <FiPause aria-hidden="true" /> : <FiPlay aria-hidden="true" />}<span>{playing ? 'Pause' : 'Play'} background</span>
      </button>}
    </div>
  </section>;
}

export default function AboutUsBanner({ reducedMotion = false }) {
  return <>
    <VoicesInteriorScene as="section" scene="pair" className="vc-hero" aria-labelledby="vc-company-title">
      <nav aria-label="Breadcrumb" className="vc-breadcrumb"><Link to="/">Home</Link><span aria-hidden="true">/</span><span aria-current="page">Who We Are</span></nav>
      <p className="vc-label">Who we are</p>
      <h1 id="vc-company-title">Built on trust.<br /><em>Powered by people.</em></h1>
      <p className="vc-hero-description">Our purpose is to inspire the togetherness of Trust, Innovation and People to create, build and solve together.</p>
      <Link className="vc-button" to="/work">Meet the work <FiArrowUpRight aria-hidden="true" /></Link>
    </VoicesInteriorScene>
    <CompanyAtmosphere reducedMotion={reducedMotion} />
  </>;
}
